import ChatActivity from "./ChatActivity";
import NeoAvatar from "./NeoAvatar";
import { Box, Button, Chip, Typography } from "@mui/material";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import { chatbotGradients, chatbotPalette } from "./chatbotTheme";

function MarkdownContent({ text = "", color }) {
  const baseColor = color || chatbotPalette.textMain;

  const nodes = [];
  const lines = text.split("\n");
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Render backend Markdown tables in a scrollable region, without fabricating data.
    if (line.includes("|") && /^\s*\|?\s*:?-{3,}:?\s*\|/.test(lines[i + 1] || "")) {
      const cells = (row) => row.trim().replace(/^\||\|$/g, "").split("|").map(cell => cell.trim());
      const headings = cells(line);
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) rows.push(cells(lines[i++]));
      nodes.push(
        <Box key={`table-${i}`} role="region" aria-label="ตารางข้อมูลในคำตอบ" tabIndex={0} sx={{ overflowX: "auto", maxWidth: "100%", my: 1 }}>
          <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", fontSize: 13, "& th, & td": { p: 1, textAlign: "left", borderBottom: `1px solid ${chatbotPalette.borderSoft}`, minWidth: 90 }, "& th": { color: chatbotPalette.teal } }}>
            <thead><tr>{headings.map((cell, index) => <th key={index} scope="col">{renderInline(cell)}</th>)}</tr></thead>
            <tbody>{rows.map((row, index) => <tr key={index}>{headings.map((_, col) => <td key={col}>{renderInline(row[col] || "")}</td>)}</tr>)}</tbody>
          </Box>
        </Box>
      );
      continue;
    }

    // H1
    if (/^#\s+/.test(line)) {
      nodes.push(
        <Typography key={i} sx={{ color: baseColor, fontWeight: 700, fontSize: 18, mt: 1.5, mb: 0.5, lineHeight: 1.35 }}>
          {renderInline(line.replace(/^#\s+/, ""))}
        </Typography>
      );
      i++;
      continue;
    }

    // H2
    if (/^##\s+/.test(line)) {
      nodes.push(
        <Typography key={i} sx={{ color: chatbotPalette.mint, fontWeight: 700, fontSize: 16, mt: 1.5, mb: 0.5, lineHeight: 1.35 }}>
          {renderInline(line.replace(/^##\s+/, ""))}
        </Typography>
      );
      i++;
      continue;
    }

    // H3
    if (/^###\s+/.test(line)) {
      nodes.push(
        <Typography key={i} sx={{ color: baseColor, fontWeight: 700, fontSize: 14.5, mt: 1, mb: 0.4, lineHeight: 1.35 }}>
          {renderInline(line.replace(/^###\s+/, ""))}
        </Typography>
      );
      i++;
      continue;
    }

    // Bullet list — collect consecutive items
    if (/^[-*]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ""));
        i++;
      }
      nodes.push(
        <Box key={`ul-${i}`} component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0.5, listStyleType: "disc" }}>
          {items.map((item, idx) => (
            <Box key={idx} component="li" sx={{ color: baseColor, fontSize: 14, lineHeight: 1.65 }}>
              {renderInline(item)}
            </Box>
          ))}
        </Box>
      );
      continue;
    }

    // Numbered list — collect consecutive items
    if (/^\d+\.\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ""));
        i++;
      }
      nodes.push(
        <Box key={`ol-${i}`} component="ol" sx={{ pl: 2.5, mt: 0.5, mb: 0.5, listStyleType: "decimal" }}>
          {items.map((item, idx) => (
            <Box key={idx} component="li" sx={{ color: baseColor, fontSize: 14, lineHeight: 1.65 }}>
              {renderInline(item)}
            </Box>
          ))}
        </Box>
      );
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(line.trim())) {
      nodes.push(<Box key={i} sx={{ borderTop: `1px solid ${chatbotPalette.borderSoft}`, my: 1 }} />);
      i++;
      continue;
    }

    // Empty line — small spacer
    if (line.trim() === "") {
      nodes.push(<Box key={i} sx={{ height: 6 }} />);
      i++;
      continue;
    }

    // Normal paragraph
    nodes.push(
      <Typography key={i} sx={{ color: baseColor, fontSize: 14, lineHeight: 1.7, wordBreak: "break-word" }}>
        {renderInline(line)}
      </Typography>
    );
    i++;
  }

  return <Box sx={{ display: "flex", flexDirection: "column" }}>{nodes}</Box>;
}

function renderInline(text) {
  // Handle **bold**, *italic*, `code`
  const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`)/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[0].startsWith("**")) {
      parts.push(<Box key={match.index} component="strong" sx={{ fontWeight: 700, color: "inherit" }}>{match[2]}</Box>);
    } else if (match[0].startsWith("*")) {
      parts.push(<Box key={match.index} component="em" sx={{ fontStyle: "italic", color: "inherit" }}>{match[3]}</Box>);
    } else {
      parts.push(
        <Box
          key={match.index}
          component="code"
          sx={{
            fontFamily: "monospace",
            fontSize: 12,
            backgroundColor: "rgba(255,255,255,0.1)",
            borderRadius: 0.5,
            px: 0.5,
            color: chatbotPalette.mint,
          }}
        >
          {match[4]}
        </Box>
      );
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}


const promptMap = {
  oee_production: [
    "สรุป OEE วันนี้ให้หน่อย",
    "Availability ต่ำเพราะอะไร",
    "มี downtime จุดไหนที่น่าจับตา",
  ],
  oee_quality: [
    "คุณภาพมีจุดไหนผิดปกติบ้าง",
    "ช่วยสรุป quality impact วันนี้",
    "มี defect trend อะไรที่ควรจับตา",
  ],
  oee_maintenance: [
    "เครื่องไหนกระทบ OEE มากสุด",
    "ช่วยสรุป downtime จากเครื่องจักร",
    "maintenance จุดไหนควรเร่งก่อน",
  ],
  default: [
    "สรุป OEE วันนี้ให้หน่อย",
    "ช่วยเล่าแนวโน้มของไลน์นี้",
    "มีอะไรผิดปกติที่ควรระวังบ้าง",
  ],
};

function normalizeSuggestedQuestion(text = "") {
  return String(text || "")
    .trim()
    .replace(/^[-*•\u2022\d.)\s]+/, "")
    .replace(/^\|+\s*/, "")
    .replace(/\s*\|+$/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function extractSuggestedQuestionsFromText(text = "") {
  if (!text) {
    return [];
  }

  const lines = String(text || "").replace(/\r\n/g, "\n").split("\n");
  const headingIndex = lines.findIndex((line) => /คำถาม\s*ถัดไป\s*ที่\s*แนะนำ/i.test(line));

  if (headingIndex < 0) {
    return [];
  }

  const suggested = [];

  for (let i = headingIndex + 1; i < lines.length; i += 1) {
    const rawLine = lines[i];
    const trimmed = rawLine.trim();

    if (!trimmed) {
      if (suggested.length > 0) {
        break;
      }
      continue;
    }

    if (/^[-*•\u2022]\s+/.test(trimmed) || /^\d+[.)]\s+/.test(trimmed)) {
      const normalized = normalizeSuggestedQuestion(trimmed);
      if (normalized) {
        suggested.push(normalized);
      }
      continue;
    }

    // Stop when we already started collecting suggestions and meet a non-list line.
    if (suggested.length > 0) {
      break;
    }
  }

  return Array.from(new Set(suggested)).slice(0, 4);
}

export default function ChatMessageList({
  canRetry,
  error,
  liveAssistantMessage,
  messages,
  onPromptSelect,
  onRetry,
  pageContext,
  pending,
  pendingSince,
  thinkingStep,
}) {
  const endRef = useRef(null);
  const emptyPrompts = promptMap[pageContext?.page] || promptMap.default;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [hideSuggestedPrompts, setHideSuggestedPrompts] = useState(false);
  const hasAssistantResponse = messages.some((message) => message.role === "assistant");
  const showPostAnswerSuggestions = hasAssistantResponse && !pending;

  const latestAssistantText = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === "assistant") {
        return messages[i]?.content || "";
      }
    }
    return "";
  }, [messages]);

  const suggestedPrompts = useMemo(() => {
    const extracted = extractSuggestedQuestionsFromText(latestAssistantText);
    return extracted.length > 0 ? extracted : emptyPrompts;
  }, [emptyPrompts, latestAssistantText]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [liveAssistantMessage, messages, pending]);

  useEffect(() => {
    if (!pending || !pendingSince) {
      setElapsedSeconds(0);
      return undefined;
    }

    const updateElapsed = () => {
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - pendingSince) / 1000)));
    };

    updateElapsed();
    const intervalId = window.setInterval(updateElapsed, 1000);

    return () => window.clearInterval(intervalId);
  }, [pending, pendingSince]);

  useEffect(() => {
    if (messages.length === 0) {
      setHideSuggestedPrompts(false);
    }
  }, [messages.length]);

  const handlePromptClick = (prompt) => {
    setHideSuggestedPrompts(true);
    onPromptSelect(prompt);
  };

  if (messages.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          justifyContent: "safe center",
          minHeight: 0,
          width: "100%",
          overflowY: "auto",
          py: 1,
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <NeoAvatar size={112} />
          <Typography sx={{ color: chatbotPalette.textMain, fontSize: 21, fontWeight: 700, mt: 1 }}>
            วันนี้อยากให้Neo ช่วยดูอะไรครับ?
          </Typography>
          <Typography sx={{ color: chatbotPalette.textMuted, fontSize: 15, mt: 0.65 }}>
            ถามเรื่อง OEE, availability, downtime หรือแนวโน้มของหน้าปัจจุบันได้เลย
          </Typography>
          {pageContext?.line ? (
            <Typography sx={{ color: "rgba(189,244,241,0.88)", fontSize: 12.5, mt: 0.75 }}>
              กำลังดู {String(pageContext.line).replace(/^line\s*/i, "ไลน์ ")}
            </Typography>
          ) : null}
        </Box>

        {hideSuggestedPrompts ? null : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
            {emptyPrompts.map((prompt) => (
              <Chip
                key={prompt}
                label={prompt}
                onClick={() => handlePromptClick(prompt)}
                sx={{
                  width: "100%", height: "auto", minHeight: 46, borderRadius: 2, py: 1,
                  "& .MuiChip-label": { whiteSpace: "normal", textAlign: "left", width: "100%" },
                  color: chatbotPalette.mint,
                  backgroundColor: "rgba(112, 239, 210, 0.08)",
                  border: `1px solid ${chatbotPalette.borderSoft}`,
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        gap: 1.2,
        overflowY: "auto",
        "& > *": { flexShrink: 0 },
        pr: 0.75,
        overscrollBehavior: "contain",
      }}
    >
      {messages.map((message) => {
        const isAssistant = message.role === "assistant";

        return (
          <Box
            key={message.id}
            sx={{
              display: "flex",
              justifyContent: isAssistant ? "flex-start" : "flex-end",
              animation: "chatbotBubbleIn 180ms ease",
            }}
          >
            <Box
              sx={{
                maxWidth: isAssistant ? "100%" : "90%",
                minWidth: 0,
                borderRadius: 4,
                px: 1.35,
                py: 1.1,
                background: isAssistant ? chatbotGradients.assistantBubble : chatbotGradients.userBubble,
                border: isAssistant
                  ? `1px solid ${chatbotPalette.borderSoft}`
                  : "1px solid rgba(255,255,255,0.09)",
                boxShadow: isAssistant
                  ? "0 4px 14px rgba(12, 27, 40, 0.10)"
                  : "none",
                backdropFilter: "blur(10px)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                <Box
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    background: isAssistant
                      ? "rgba(232, 255, 251, 0.16)"
                      : "rgba(26,83,73,0.12)",
                    color: isAssistant ? chatbotPalette.mint : "#214F47",
                  }}
                >
                  {isAssistant ? (
                    <NeoAvatar size={28} />
                  ) : (
                    <PersonRoundedIcon fontSize="small" />
                  )}
                </Box>
                <Typography sx={{ color: isAssistant ? chatbotPalette.textMain : "#214F47", fontWeight: 700, fontSize: 15 }}>
                  {isAssistant ? "Neo" : "คุณ"}
                </Typography>
                <Typography sx={{ color: isAssistant ? "#C4D7E3" : "#456B62", fontSize: 12 }}>
                  {dayjs().format("HH:mm")}
                </Typography>
              </Box>
              {isAssistant && message.thinkingSteps && message.thinkingSteps.length > 0 ? (
                <ChatActivity
                  steps={message.thinkingSteps}
                  durationSec={message.thinkingDurationSec || 0}
                  isLive={false}
                />
              ) : null}
              {isAssistant ? (
                <MarkdownContent
                  text={message.content}
                  color={chatbotPalette.textMain}
                />
              ) : (
                <Typography
                  sx={{
                    color: "#183F37",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {message.content}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}

      {liveAssistantMessage || pending ? (
        <Box
          sx={{
            maxWidth: "100%",
            minWidth: 0,
            borderRadius: 4,
            px: 1.4,
            py: 1.25,
            minHeight: 0,
            background: liveAssistantMessage?.mode === "final" ? chatbotGradients.assistantBubble : "transparent",
            border: liveAssistantMessage?.mode === "final" ? `1px solid ${chatbotPalette.borderSoft}` : "none",
            boxShadow: "none",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: "rgba(232, 255, 251, 0.16)",
              }}
            >
              <NeoAvatar size={28} />
            </Box>
            <Typography sx={{ color: chatbotPalette.textMain, fontWeight: 700, fontSize: 15 }}>Neo</Typography>
          </Box>
          {liveAssistantMessage?.mode === "final" ? (
            <MarkdownContent
              text={liveAssistantMessage.content}
              color={chatbotPalette.textMain}
            />
          ) : (
            <Box sx={{ minHeight: 52, display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
              <ChatActivity
                steps={liveAssistantMessage?.thinkingSteps || []}
                liveStep={liveAssistantMessage?.statusText || thinkingStep}
                durationSec={liveAssistantMessage?.thinkingDurationSec || elapsedSeconds}
                isLive={pending}
                failed={Boolean(error)}
              />
            </Box>
          )}
        </Box>
      ) : null}

      {error ? (
        <Box sx={{ px: 0.5, pt: 0.5, pb: 0.75, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.2, border: "1px solid rgba(255, 180, 180, 0.35)", borderRadius: 2, backgroundColor: "rgba(93, 31, 31, 0.24)" }}>
          <Typography sx={{ color: chatbotPalette.danger, fontSize: 13.5, px: 0.75 }}>
            {error}
          </Typography>
          {canRetry ? (
            <Button
              onClick={onRetry}
              disabled={pending}
              sx={{
                color: chatbotPalette.tealSoft,
                fontSize: 12.5,
                textTransform: "none",
                minWidth: 0,
                pr: 0.75,
              }}
            >
              ลองอีกครั้ง
            </Button>
          ) : null}
        </Box>
      ) : null}

      {showPostAnswerSuggestions ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "flex-end", pt: 0.5, pb: 0.25 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8, alignItems: "flex-end", maxWidth: { xs: "100%", md: "72%" } }}>
            {suggestedPrompts.map((prompt, index) => (
              <Chip
                key={prompt}
                label={prompt}
                onClick={() => handlePromptClick(prompt)}
                sx={{
                  height: "auto",
                  borderRadius: "12px",
                  px: 0.8,
                  py: 0.55,
                  color: "#D7F6EB",
                  fontWeight: 600,
                  fontSize: 14,
                  backgroundColor: "rgba(112,239,210,0.06)",
                  border: "1px solid #719E9C",
                  boxShadow: "none",
                  animation: `neoSuggestionIn 240ms ease ${index * 55}ms both`,
                  "& .MuiChip-label": {
                    whiteSpace: "normal",
                    display: "block",
                    textAlign: "center",
                    py: 0.2,
                  },
                  "&:hover": {
                    backgroundColor: "rgba(112,239,210,0.16)",
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      ) : null}

      <Box ref={endRef} />
      <style>{`@keyframes chatbotBubbleIn {from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); }} @keyframes chatbotFadeIn {from { opacity: 0; } to { opacity: 1; }} @keyframes chatbotDot {0%, 80%, 100% { transform: translateY(0); opacity: 0.45; } 40% { transform: translateY(-3px); opacity: 1; }} @keyframes neoSuggestionIn {from { opacity: 0; transform: translateY(8px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); }}`}</style>
    </Box>
  );
}
